/**
 * The settings screen.
 *
 * Every row renders its `effect` string underneath it — the literal sentence
 * describing what changing it does. That is not decoration: this project has
 * twice shipped controls that looked live and were not, and a row that has to
 * name its own mechanism is a row somebody would have had to lie in writing to
 * ship dead.
 *
 * The screen is a projection of `settingsModel.ts` and nothing else. It knows
 * no game state, applies nothing, and reports each change to the integrator,
 * which is what applies it and persists it. Same contract as every other panel
 * here.
 */

import { el, injectStyle } from './style';
import { injectShellStyle } from './shellStyle';
import {
  REBINDABLE,
  RESERVED_CODES,
  RESERVED_PAD_BUTTONS,
  SETTING_DEFS,
  cycleSetting,
  isBindableCode,
  isBindablePadButton,
  keyLabel,
  padButtonLabel,
  settingDef,
  type BindingRow,
  type PadBindingRow,
  type SettingId,
  type SettingsState,
} from './settingsModel';

export interface SettingsCallbacks {
  /** A row moved. The integrator applies, persists, and hands state back. */
  onChange?(id: SettingId, value: string): void;
  /**
   * A key row was given a new code, or `null` to put the default back. Same
   * contract as `onChange`: the integrator resolves it against the real binding
   * table, applies it to the live `Input`, persists it, and hands the result
   * back through `update`. The screen never guesses which key it ended on,
   * because taking a key from one action gives it to no other.
   */
  onRebind?(action: string, code: string | null): void;
  /** The pad twin of `onRebind` — issue #191. `button` is `null` to unbind. */
  onRebindPad?(action: string, button: number | null): void;
  /**
   * A pad-capture row started or stopped listening. The integrator uses this
   * to point `ShellPad.startButtonCapture`/`stopButtonCapture` at the row —
   * the screen itself never touches a pad, the same separation `onRebind`
   * already keeps from the keyboard.
   */
  onPadListenChange?(active: boolean): void;
  onClose?(): void;
}

export interface SettingsChrome {
  /** What the back button says — 'Back to title' vs 'Back to the run'. */
  backLabel: string;
  /** One row per rebindable action, already resolved by `settingsModel`. */
  bindings: readonly BindingRow[];
  /** The pad twin of `bindings` — issue #191. */
  padBindings: readonly PadBindingRow[];
  /**
   * Things that are deliberately not on this screen, and why. Rendered as a
   * footnote. An honest absence is worth more than a dead slider, but only if
   * the player can tell the difference between "missing" and "forgotten".
   */
  omissions: readonly string[];
}

export class SettingsScreen {
  readonly root: HTMLElement;

  private hostEl: HTMLElement;
  private cb: SettingsCallbacks;
  private ownsRootClass = false;

  private rowsHost: HTMLElement;
  private keysHost: HTMLElement;
  private padKeysHost: HTMLElement;
  private note: HTMLElement;
  private backBtn: HTMLButtonElement;

  private rows: HTMLElement[] = [];
  private valEls: HTMLElement[] = [];
  /**
   * The key rows, appended after the setting rows in one shared cursor space.
   * They are a different *kind* of row — press a key rather than cycle a value
   * — but they are on the same screen, so ↑↓ has to walk through both or half
   * the screen is unreachable without a mouse.
   */
  private keyRows: HTMLElement[] = [];
  private keyVals: HTMLElement[] = [];
  /**
   * The pad rows — issue #191. A third kind of row in the same shared cursor
   * space as the setting rows and the key rows, for the reason the key rows
   * joined that space in the first place: ↑↓ has to reach every row or a
   * controller-only player cannot reach half the screen.
   */
  private padRows: HTMLElement[] = [];
  private padVals: HTMLElement[] = [];
  private cursor = 0;
  /** The action whose next keystroke is being captured, or null. */
  private listening: string | null = null;
  /** The action whose next pad button is being captured, or null. */
  private listeningPad: string | null = null;
  /** Shown under the key rows: the refusal, or the instruction. */
  private keyHint: HTMLElement;
  /** Shown under the pad rows: the refusal, or the instruction. */
  private padHint: HTMLElement;
  private bindings: readonly BindingRow[] = [];
  private padBindings: readonly PadBindingRow[] = [];
  /** The last state rendered. The screen's only memory, and it is a copy. */
  private shown: SettingsState | null = null;

  private listeners: Array<[EventTarget, string, EventListener, boolean]> = [];

  constructor(host: HTMLElement, cb: SettingsCallbacks = {}) {
    this.hostEl = host;
    this.cb = cb;
    injectStyle();
    injectShellStyle();
    if (!host.closest('.ui-root')) {
      host.classList.add('ui-root');
      this.ownsRootClass = true;
    }

    this.root = el('div', 'ui-layer sh-layer');
    this.root.style.pointerEvents = 'auto';
    this.root.tabIndex = -1;
    this.root.hidden = true;
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-label', 'Settings');

    const box = el('div', 'sh-box');

    const head = el('div', 'sh-head');
    const h2 = document.createElement('h2');
    h2.textContent = 'Settings';
    head.append(h2, el('div', 'ui-grow'));
    this.backBtn = document.createElement('button');
    this.backBtn.className = 'sh-item';
    this.backBtn.type = 'button';
    this.backBtn.style.width = 'auto';
    this.backBtn.append(el('b', '', 'Back'));
    this.backBtn.addEventListener('click', () => this.cb.onClose?.());
    head.append(this.backBtn);

    this.rowsHost = el('div', 'sh-rows');
    this.note = el('div', 'sh-note');

    // Built once: the definition table is static, so only the value cells and
    // the cursor ever change.
    for (const d of SETTING_DEFS) {
      const b = document.createElement('button');
      b.className = 'sh-row';
      b.type = 'button';
      const val = el('span', 'sh-row__val', '—');
      b.append(
        el('span', 'sh-row__name', d.label),
        val,
        el('span', 'sh-row__why', d.effect),
      );
      b.addEventListener('click', (e) => {
        this.cursor = SETTING_DEFS.indexOf(d);
        this.paint();
        // Shift-click steps backwards, so a four-option row is two clicks from
        // anywhere rather than three.
        this.step(d.id, (e as MouseEvent).shiftKey ? -1 : 1);
      });
      b.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        this.cursor = SETTING_DEFS.indexOf(d);
        this.paint();
        this.step(d.id, -1);
      });
      this.rowsHost.append(b);
      this.rows.push(b);
      this.valEls.push(val);
    }

    // ---- controls
    //
    // A second block rather than six more `SETTING_DEFS`: a binding is not a
    // cycle through a fixed option list, it is "press the key you want". The
    // rows share the cursor and the stylesheet, and nothing else.
    const keysHost = el('div', 'sh-rows');
    for (const r of REBINDABLE) {
      const b = document.createElement('button');
      b.className = 'sh-row';
      b.type = 'button';
      const val = el('span', 'sh-row__val', '—');
      b.append(el('span', 'sh-row__name', r.label), val);
      b.addEventListener('click', () => {
        this.cursor = SETTING_DEFS.length + REBINDABLE.indexOf(r);
        this.listen(r.action);
      });
      keysHost.append(b);
      this.keyRows.push(b);
      this.keyVals.push(val);
    }

    const keysHead = el('div', 'sh-head');
    const h3 = document.createElement('h2');
    h3.textContent = 'Controls';
    keysHead.append(h3, el('div', 'ui-grow'));
    this.keysHost = keysHost;
    this.keyHint = el('div', 'sh-note');
    this.keyHint.textContent = this.idleHint;

    // ---- pad controls — issue #191
    //
    // The pad twin of the block above: same shared cursor space, same "press
    // the thing you want" shape, capturing a button index instead of a code.
    const padKeysHost = el('div', 'sh-rows');
    for (const r of REBINDABLE) {
      const b = document.createElement('button');
      b.className = 'sh-row';
      b.type = 'button';
      const val = el('span', 'sh-row__val', '—');
      b.append(el('span', 'sh-row__name', r.label), val);
      b.addEventListener('click', () => {
        this.cursor = SETTING_DEFS.length + REBINDABLE.length + REBINDABLE.indexOf(r);
        this.listenPad(r.action);
      });
      padKeysHost.append(b);
      this.padRows.push(b);
      this.padVals.push(val);
    }

    const padKeysHead = el('div', 'sh-head');
    const h4 = document.createElement('h2');
    h4.textContent = 'Pad controls';
    padKeysHead.append(h4, el('div', 'ui-grow'));
    this.padKeysHost = padKeysHost;
    this.padHint = el('div', 'sh-note');
    this.padHint.textContent = this.idlePadHint;

    const foot = el('div', 'sh-foot');
    foot.append(
      el('span', '', '↑↓ row'),
      el('span', '', '←→ / Enter change'),
      el('span', '', 'Esc back'),
      el('span', '', 'Pad: d-pad row, A change, B back'),
    );

    box.append(
      head,
      this.rowsHost,
      keysHead,
      this.keysHost,
      this.keyHint,
      padKeysHead,
      this.padKeysHost,
      this.padHint,
      this.note,
      foot,
    );
    this.root.append(box);
    host.append(this.root);

    this.on(this.root, 'keydown', ((e: KeyboardEvent) => this.onKey(e)) as EventListener);
  }

  private on(t: EventTarget, type: string, fn: EventListener, capture = false): void {
    t.addEventListener(type, fn, capture);
    this.listeners.push([t, type, fn, capture]);
  }

  update(state: Readonly<SettingsState>, chrome: SettingsChrome): void {
    this.shown = { ...state };
    this.bindings = chrome.bindings;
    this.padBindings = chrome.padBindings;
    for (let i = 0; i < SETTING_DEFS.length; i++) {
      const d = SETTING_DEFS[i];
      const cur = state[d.id];
      const opt = d.options.find((o) => o.value === cur) ?? d.options[0];
      const label = `◂ ${opt.label} ▸`;
      if (this.valEls[i].textContent !== label) this.valEls[i].textContent = label;
    }

    const back = this.backBtn.firstElementChild as HTMLElement;
    if (back.textContent !== chrome.backLabel) back.textContent = chrome.backLabel;

    const sig = chrome.omissions.join('|');
    if (this.note.dataset.sig !== sig) {
      this.note.dataset.sig = sig;
      this.note.textContent = '';
      this.note.append(el('b', '', 'Not here, and why:'));
      for (const o of chrome.omissions) this.note.append(el('div', '', o));
    }
    this.paint();
  }

  private paint(): void {
    for (let i = 0; i < this.rows.length; i++) {
      const on = i === this.cursor ? '1' : '0';
      if (this.rows[i].dataset.on !== on) this.rows[i].dataset.on = on;
    }
    const base = this.rows.length;
    for (let i = 0; i < this.keyRows.length; i++) {
      const on = base + i === this.cursor ? '1' : '0';
      if (this.keyRows[i].dataset.on !== on) this.keyRows[i].dataset.on = on;
      const row = this.bindings[i];
      const action = REBINDABLE[i].action;
      // While listening the row says so rather than showing a key it is about
      // to stop having — the value cell is the only place the screen can admit
      // it is waiting.
      const label =
        this.listening === action
          ? '◂ press a key ▸'
          : row
            ? row.key
            : '—';
      if (this.keyVals[i].textContent !== label) this.keyVals[i].textContent = label;
      const unbound = row && row.code === null ? '1' : '0';
      if (this.keyVals[i].dataset.unbound !== unbound) this.keyVals[i].dataset.unbound = unbound;
    }
    const base2 = base + this.keyRows.length;
    for (let i = 0; i < this.padRows.length; i++) {
      const on = base2 + i === this.cursor ? '1' : '0';
      if (this.padRows[i].dataset.on !== on) this.padRows[i].dataset.on = on;
      const row = this.padBindings[i];
      const action = REBINDABLE[i].action;
      const label =
        this.listeningPad === action
          ? '◂ press a pad button ▸'
          : row
            ? row.key
            : '—';
      if (this.padVals[i].textContent !== label) this.padVals[i].textContent = label;
      const unbound = row && row.button === null ? '1' : '0';
      if (this.padVals[i].dataset.unbound !== unbound) this.padVals[i].dataset.unbound = unbound;
    }
  }

  /** Start capturing for `action`. Only one row can listen at a time. */
  private listen(action: string): void {
    this.listening = action;
    this.setHint(`Press the key for ${action}. Backspace restores the default, Escape cancels.`);
    this.paint();
  }

  private setHint(text: string): void {
    if (this.keyHint.textContent !== text) this.keyHint.textContent = text;
  }

  private readonly idleHint =
    'Click a row (or Enter) then press the key you want. Backspace restores the default, ' +
    'Escape cancels. Movement, Tab, Esc, C and M are not here — see below.';

  /**
   * Start capturing a pad button for `action` — issue #191. The pad twin of
   * `listen`. Reports through `onPadListenChange` so the integrator can point
   * `ShellPad.startButtonCapture` at this row; the screen itself never touches
   * `navigator.getGamepads()`, the same separation `onRebind` already keeps.
   */
  private listenPad(action: string): void {
    this.listeningPad = action;
    this.setPadHint(`Press the pad button for ${action}. Escape cancels.`);
    this.cb.onPadListenChange?.(true);
    this.paint();
  }

  /** Stop capturing, whether or not a button was accepted. */
  private stopListeningPad(): void {
    if (this.listeningPad === null) return;
    this.listeningPad = null;
    this.setPadHint(this.idlePadHint);
    this.cb.onPadListenChange?.(false);
  }

  private setPadHint(text: string): void {
    if (this.padHint.textContent !== text) this.padHint.textContent = text;
  }

  private readonly idlePadHint =
    'Click a row (or Enter, then the button you want) to capture a pad button. Escape cancels. ' +
    'D-pad, confirm/back and the shoulders are not here — see below.';

  /**
   * Consume a raw pad button aimed at a listening pad row — issue #191. The
   * pad twin of `captureKey`, called by the integrator (through
   * `ShellPad.startButtonCapture`'s callback) rather than by a DOM event,
   * because a pad button has no keydown to listen for. Same refusal shape as
   * the keyboard: a reserved button is refused and the row keeps listening,
   * so the player's next press still lands where they meant it to.
   */
  capturePadButton(button: number): void {
    const action = this.listeningPad;
    if (action === null) return;
    if (!isBindablePadButton(button)) {
      this.setPadHint(
        RESERVED_PAD_BUTTONS.includes(button)
          ? `Button ${button} drives this screen and every menu behind it, so binding it here ` +
            'would only half-take. Press another button.'
          : 'That button cannot be bound. Press another.',
      );
      // Refused, not cancelled: `listeningPad` stays set and `ShellPad`'s
      // capture callback stays registered (it is cleared only by
      // `stopListeningPad`), so the very next press gets another chance.
      this.paint();
      return;
    }
    this.stopListeningPad();
    this.cb.onRebindPad?.(action, button);
    this.paint();
  }

  /**
   * Consume a keystroke aimed at a listening row.
   *
   * Returns true when it was handled, which is what stops the same key also
   * moving the cursor. A reserved code is refused *and stays listening*, so the
   * player's next press still lands where they meant it to — a refusal that
   * also cancelled would read as the screen ignoring them.
   */
  private captureKey(e: KeyboardEvent): boolean {
    const action = this.listening;
    if (action === null) return false;
    if (e.code === 'Escape') {
      this.listening = null;
      this.setHint(this.idleHint);
    } else if (e.code === 'Backspace' || e.code === 'Delete') {
      this.listening = null;
      this.setHint(this.idleHint);
      this.cb.onRebind?.(action, null);
    } else if (!isBindableCode(e.code)) {
      // Still listening, deliberately: the next press should land where the
      // player meant it to. A refusal that also cancelled would read as the
      // screen having ignored them.
      this.setHint(
        RESERVED_CODES.includes(e.code)
          ? `${keyLabel(e.code)} is read directly by the interface as well as by the game, ` +
            'so binding it here would only half-take. Pick another key.'
          : 'That key cannot be bound. Pick another.',
      );
    } else {
      this.listening = null;
      this.setHint(this.idleHint);
      this.cb.onRebind?.(action, e.code);
    }
    this.paint();
    return true;
  }

  /**
   * Report the next value. The integrator decides whether it sticks, and hands
   * the result back through `update` — which is what makes a double-click land
   * on the value the game actually has rather than on the one the screen
   * guessed.
   */
  private step(id: SettingId, dir: 1 | -1): void {
    const cur = this.shown?.[id] ?? settingDef(id).default;
    this.cb.onChange?.(id, cycleSetting(id, cur, dir));
  }

  /**
   * Drive the screen from a gamepad — issue #78.
   *
   * A separate door from `onKey` for one reason, and it is not symmetry: a
   * listening key row binds whatever code arrives, and `isBindableCode` says
   * yes to `ArrowDown` and to `Enter` — both of them plain letters, neither of
   * them reserved. A pad routed straight into `onKey` would therefore *rebind
   * jump to ArrowDown* the moment the player pressed down to leave a row they
   * had opened by accident, and the profile would keep it.
   *
   * So while a row is listening the pad can do exactly one thing: cancel. That
   * is honest rather than restrictive — the row is asking for a key, a pad has
   * none to give, and the way out has to work or the screen is a trap for a
   * controller-only player.
   */
  handlePadKey(e: KeyboardEvent): void {
    if (this.listening !== null && e.code !== 'Escape') return;
    // While a pad row is listening, `ShellPad` has stopped delivering
    // translated intents at all (`startButtonCapture` suppresses `pad.poll`)
    // and is feeding raw buttons to `capturePadButton` instead — so this
    // never actually fires during a pad capture. The guard is here anyway, for
    // the same reason `listening`'s guard is: a caller that changes how the
    // two are wired should not also have to remember this invariant.
    if (this.listeningPad !== null) return;
    this.onKey(e);
  }

  private onKey(e: KeyboardEvent): void {
    // A listening row owns the keyboard outright: every other branch below
    // would otherwise steal the very keys most worth binding.
    if (this.captureKey(e)) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    // A pad row being captured is driven by raw pad buttons through
    // `capturePadButton`, not by keydown — but Escape still has to cancel it
    // from a keyboard, the same "the way out has to work" rule `handlePadKey`
    // documents for the keyboard-listening case.
    if (this.listeningPad !== null) {
      if (e.code === 'Escape') {
        this.stopListeningPad();
        this.paint();
      }
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    const padBase = this.rows.length + this.keyRows.length;
    const n = padBase + this.padRows.length;
    if (n === 0) return;
    const onPadRow = this.cursor >= padBase;
    const onKeyRow = !onPadRow && this.cursor >= this.rows.length;
    const keyIndex = this.cursor - this.rows.length;
    const padIndex = this.cursor - padBase;

    if (e.code === 'ArrowDown' || e.code === 'KeyS') {
      this.cursor = (this.cursor + 1) % n;
    } else if (e.code === 'ArrowUp' || e.code === 'KeyW') {
      this.cursor = (this.cursor - 1 + n) % n;
    } else if (e.code === 'ArrowRight' || e.code === 'KeyD' || e.code === 'Enter') {
      if (onPadRow) this.listenPad(REBINDABLE[padIndex].action);
      else if (onKeyRow) this.listen(REBINDABLE[keyIndex].action);
      else this.step(SETTING_DEFS[this.cursor].id, 1);
    } else if (e.code === 'ArrowLeft' || e.code === 'KeyA') {
      if (onPadRow) this.listenPad(REBINDABLE[padIndex].action);
      else if (onKeyRow) this.listen(REBINDABLE[keyIndex].action);
      else this.step(SETTING_DEFS[this.cursor].id, -1);
    } else if (e.code === 'Escape') {
      this.cb.onClose?.();
    } else if (e.code === 'Backspace') {
      // On a key or pad row Backspace is "put the default back", the same verb
      // it has while listening. Anywhere else it is still "go back".
      if (onPadRow) this.cb.onRebindPad?.(REBINDABLE[padIndex].action, null);
      else if (onKeyRow) this.cb.onRebind?.(REBINDABLE[keyIndex].action, null);
      else this.cb.onClose?.();
    } else {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    this.paint();
  }

  show(): void {
    this.root.hidden = false;
    this.root.focus();
  }

  hide(): void {
    this.root.hidden = true;
    // A row left listening would swallow the first keystroke of whatever screen
    // comes next, and bind it.
    this.listening = null;
    this.setHint(this.idleHint);
    this.stopListeningPad();
  }

  destroy(): void {
    for (const [t, type, fn, capture] of this.listeners) t.removeEventListener(type, fn, capture);
    this.listeners.length = 0;
    this.root.remove();
    if (this.ownsRootClass) this.hostEl.classList.remove('ui-root');
  }
}
